import { PrimaryButton } from '../../ui/Button/Buttons';
import { UploadIcon } from '../../../icons/SvgIcons';
import { SubmittedEvidenceItem } from '../../../types/RequirementSteps.types';
import {
  evidenceField,
  formatEvidenceDate,
  latestEvidence
} from '../../../utils/evidenceHistory';
import { formatDateOnly } from '../../../utils/Date.util';

type RequirementEvidenceDocumentProps = {
  submittedEvidence?: SubmittedEvidenceItem[] | null;
  // Opens the upload dialog. The dialog itself is owned by RequirementListStep, NOT by this
  // component: Modal renders at z-10 while SideDrawer's backdrop is z-40, so a modal opened from
  // inside the drawer would render underneath it — and SideDrawer returns null when closed, which
  // would unmount the dialog along with this control.
  onRequestUpload: () => void;
  // Label for the action button. "Renew" reads better than "Replace" for a recurring obligation.
  uploadLabel?: string;
  // When false the button is hoisted to the drawer footer by the step, so this control renders
  // only the submitted-history block (and nothing at all when there is no history).
  showUploadButton?: boolean;
};

// Single source of truth for the upload label. The step footer and the inline control must agree,
// so keep the fallback here rather than duplicating the ternary.
export function evidenceUploadLabel(
  submittedEvidence: SubmittedEvidenceItem[] | null | undefined,
  uploadLabel?: string
) {
  return uploadLabel ?? ((submittedEvidence?.length ?? 0) > 0 ? 'Replace' : 'Upload');
}

export function RequirementEvidenceDocument({
  submittedEvidence,
  onRequestUpload,
  uploadLabel,
  showUploadButton = true
}: RequirementEvidenceDocumentProps) {
  const history = submittedEvidence ?? [];
  const hasHistory = history.length > 0;
  const latest = latestEvidence(history);
  // The date the document was PRODUCED (e.g. when the bias audit was conducted), as opposed to
  // `createdAt`, which is when it was filed. The two differ whenever the submitter back-dates, and
  // the produced date is the one the expiry window actually runs from — so showing only `createdAt`
  // would leave the user unable to tell why a freshly uploaded report is already expiring.
  const attestedAt = evidenceField(latest, 'attestedAt');
  // Superseded documents stay in this list ON PURPOSE. Reopening an action does not delete its
  // evidence rows (`markActionUncompleted` never touches `requirementEvidence`), so a later upload
  // appends. Do not "tidy" this by hiding older entries: an audit trail that silently drops a
  // submitted compliance document is worse than a visible superseded one.

  const label = evidenceUploadLabel(submittedEvidence, uploadLabel);

  if (!showUploadButton && !hasHistory) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 mt-2">
      {hasHistory && (
        <div className="flex flex-col gap-1 text-xs text-gray-600">
          <span className="font-medium text-gray-700">
            {attestedAt
              ? `Dated ${formatDateOnly(attestedAt)} — submitted ${formatEvidenceDate(latest?.createdAt)}`
              : `Latest document — ${formatEvidenceDate(latest?.createdAt)}`}
          </span>
          {history.length > 1 && (
            <ul className="list-disc pl-4">
              {history.slice(1).map((item) => {
                const itemAttestedAt = evidenceField(item, 'attestedAt');
                return (
                  <li key={item.id}>
                    {itemAttestedAt
                      ? `Dated ${formatDateOnly(itemAttestedAt)} — submitted ${formatEvidenceDate(item.createdAt)}`
                      : formatEvidenceDate(item.createdAt)}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {showUploadButton && (
        <div className="flex justify-end">
          <PrimaryButton
            size="sm"
            icon={UploadIcon('#111827', 16)}
            className="break-keep"
            onClick={onRequestUpload}
          >
            {label}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}
